import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from '@mui/material'
import { ReactElement, useEffect, useMemo } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { BackIcon, SaveIcon } from 'src/v3/core/icons'
import { QuizBuilder } from 'src/v3/components/quiz/quiz-builder'
import {
  fetchQuizLibrary,
  fetchSelectedQuiz,
  fetchQuestionsByQuizID,
  selectSelectedQuiz,
  selectSelectedQuizStatus,
  selectSelectedQuizQuestions,
  selectSelectedQuizQuestionsStatus,
  selectedQuizReset,
} from '../create.slice'
import { updateQuizMeta } from '../utils/api'
import { useAutoSaveMeta } from '../hooks/use-auto-save-meta'
import { CREATE_QUIZ_URLS } from '../constants'

type LocationState = {
  backLabel?: string
  backPath?: string
}

export default function QuizEditPage(): ReactElement {
  let { id } = useParams<{ id: string }>()
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()

  let state = location.state as LocationState | null
  let backLabel = state?.backLabel ?? 'Back to Quiz Library'
  let backPath = state?.backPath ?? CREATE_QUIZ_URLS.LIBRARY

  let quiz = useAppSelector(selectSelectedQuiz)
  let quizStatus = useAppSelector(selectSelectedQuizStatus)
  let questions = useAppSelector(selectSelectedQuizQuestions)
  let questionsStatus = useAppSelector(selectSelectedQuizQuestionsStatus)

  // Fetch the underlying QuizV2 for the library item
  useEffect(() => {
    if (id && quizStatus === 'uninitialized') {
      dispatch(fetchSelectedQuiz(id))
    }
  }, [id, quizStatus, dispatch])

  // Fetch questions once quiz is loaded
  useEffect(() => {
    if (quiz?.id && questionsStatus === 'uninitialized') {
      dispatch(fetchQuestionsByQuizID(quiz.id))
    }
  }, [quiz?.id, questionsStatus, dispatch])

  // Reset quiz/questions state on id change or unmount
  useEffect(
    () => () => {
      dispatch(selectedQuizReset())
    },
    [id, dispatch],
  )

  let { showError, NotificationComponent } = useNotifications()

  let initialFields = useMemo(() => ({ name: quiz?.name ?? '' }), [quiz?.name])

  let {
    fields,
    isSaving: isMetaSaving,
    hasChanges,
    handleFieldChange,
    handleManualSave,
  } = useAutoSaveMeta({
    id,
    initialFields,
    saveFn: (saveID, { name }) => updateQuizMeta(saveID, { name }),
    onSaveSuccess: () => dispatch(fetchQuizLibrary()),
    onError: (msg) => showError(msg),
  })

  let handleBack = () => {
    // Question CRUD only updates selectedQuiz/selectedQuizQuestions, not the
    // quizLibrary the course publish checklist reads questionCount from.
    dispatch(fetchQuizLibrary())
    navigate(backPath)
  }

  let isLoading = quizStatus === 'pending' || quizStatus === 'uninitialized'

  if (isLoading) {
    return (
      <Box
        className="flex items-center justify-center py-24"
        role="status"
        aria-label="Loading quiz"
      >
        <CircularProgress aria-hidden="true" />
      </Box>
    )
  }

  if (!quiz && quizStatus === 'fulfilled') {
    return (
      <Container maxWidth="xl" className="py-6">
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          aria-label={backLabel}
        >
          {backLabel}
        </Button>
        <Typography color="text.secondary" className="mt-6 text-center">
          Quiz not found.
        </Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex items-center justify-between mb-6">
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          aria-label={backLabel}
        >
          {backLabel}
        </Button>
        <Button
          variant="contained"
          startIcon={
            isMetaSaving ? (
              <CircularProgress size={14} color="inherit" aria-hidden="true" />
            ) : (
              <SaveIcon />
            )
          }
          disabled={!hasChanges || isMetaSaving || !fields.name.trim()}
          onClick={handleManualSave}
          aria-busy={isMetaSaving}
        >
          {isMetaSaving ? 'Saving...' : hasChanges ? 'Save' : 'Saved'}
        </Button>
      </Box>
      <TextField
        fullWidth
        variant="standard"
        value={fields.name}
        onChange={(e) => handleFieldChange('name', e.target.value)}
        placeholder="Name"
        slotProps={{
          input: {
            disableUnderline: true,
            inputProps: { 'aria-label': 'Quiz name' },
            sx: {
              typography: 'h4',
              '& input': { padding: 0 },
            },
          },
        }}
        sx={{ mb: 4 }}
      />
      {id && quiz && (
        <QuizBuilder
          quiz={quiz}
          fetchQuiz={fetchSelectedQuiz}
          fetchQuizBy={id}
          questionData={questions}
          fetchQuestionData={fetchQuestionsByQuizID}
          apiPath="/api/v3/create"
          onSaveSuccess={handleBack}
          submitButton={
            <Box className="flex justify-end mt-4">
              <Button type="submit" variant="contained">
                Save Quiz
              </Button>
            </Box>
          }
        />
      )}
      {id &&
        !quiz &&
        quizStatus !== 'pending' &&
        quizStatus !== 'uninitialized' && (
          <Typography color="text.secondary" className="mt-4">
            No quiz found for this item.
          </Typography>
        )}
      {NotificationComponent}
    </Container>
  )
}
